import { useState } from 'react';

// ── Types ──────────────────────────────────────────────────────

interface RuleBase {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
  tooltip?: string;
}

export interface RulePanelProps<R extends RuleBase> {
  rules: R[];
  onToggle: (ruleId: string) => void;
  suppressionCounts: Map<string, { count: number; breakdown: Map<string, number> }>;
  breakdownLabel?: string; // "kind" or "field" — for the expand button text
}

// ── Component ──────────────────────────────────────────────────

export function RulesPanel<R extends RuleBase>({
  rules,
  onToggle,
  suppressionCounts,
  breakdownLabel = 'items',
}: RulePanelProps<R>) {
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());

  return (
    <>
      {rules.map((rule) => {
        const detail = suppressionCounts.get(rule.id);
        const count = detail?.count ?? 0;
        const breakdown = detail?.breakdown;
        const sortedEntries = breakdown
          ? [...breakdown.entries()].sort((a, b) => b[1] - a[1])
          : [];
        const maxCount = sortedEntries.length > 0 ? sortedEntries[0][1] : 0;
        const isExpanded = expandedRules.has(rule.id);
        const visibleEntries = isExpanded ? sortedEntries : sortedEntries.slice(0, 5);
        const hiddenCount = Math.max(0, sortedEntries.length - 5);

        return (
          <div key={rule.id} className="space-y-1">
            {/* Top row: toggle + label + description + count */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={rule.enabled}
                className={`relative shrink-0 w-8 h-[18px] rounded-full cursor-pointer transition-colors ${
                  rule.enabled ? 'bg-blueberry-500' : 'bg-bg-tertiary'
                }`}
                onClick={() => onToggle(rule.id)}
              >
                <span
                  className={`absolute top-[2px] left-[2px] h-[14px] w-[14px] rounded-full bg-bg-raised transition-transform ${
                    rule.enabled ? 'translate-x-[14px]' : 'translate-x-0'
                  }`}
                />
              </button>
              <div className="flex-1 min-w-0" title={rule.tooltip}>
                <span className="text-xs font-semibold text-content-primary">{rule.label}</span>
                {rule.description && (
                  <span className="ml-2 text-xs text-content-secondary">{rule.description}</span>
                )}
              </div>
              {count > 0 && (
                <span className="shrink-0 text-[11px] text-content-tertiary">
                  Matches {count}
                </span>
              )}
            </div>

            {/* Bar chart breakdown */}
            {count > 0 && sortedEntries.length > 0 && (
              <div className="ml-11 space-y-0.5">
                {visibleEntries.map(([name, n]) => (
                  <div key={name} className="flex items-center gap-1.5">
                    <span className="text-[10px] text-content-tertiary font-mono truncate text-right w-[140px] shrink-0">
                      {name}
                    </span>
                    <div className="flex-1 bg-bg-tertiary rounded-sm h-3.5">
                      <div
                        className="bg-blueberry-400 rounded-sm h-full"
                        style={{ width: `${(n / maxCount) * 100}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-content-tertiary w-7 text-right shrink-0">{n}</span>
                  </div>
                ))}
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    className="text-[10px] text-content-action cursor-pointer hover:underline ml-[140px] pl-1.5"
                    onClick={() =>
                      setExpandedRules((prev) => {
                        const next = new Set(prev);
                        if (next.has(rule.id)) {
                          next.delete(rule.id);
                        } else {
                          next.add(rule.id);
                        }
                        return next;
                      })
                    }
                  >
                    {isExpanded ? 'Show less' : `+${hiddenCount} more ${breakdownLabel}`}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
